import { trpc } from '@/trpcClient';
import { cn } from '@/utils/utils';
import { MaterialDto } from '@shelflix/trpc/routes/material/list/useCase';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card } from '@shelflix/ui-web/components/ui/card';
import { Skeleton } from '@shelflix/ui-web/components/ui/skeleton';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function MaterialCard({
  establishment,
  materials,
  onOpen,
}: {
  establishment: string;
  materials: MaterialDto[];
  handleOpenVideoModal?: (materialId: string) => void;
  onOpen?: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const getStatusDisplayLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <div className="text-status-active flex flex-row justify-end">Ligada</div>;
      case 'OPERATIONALONLINE':
        return <div className="text-status-active flex flex-row justify-end">Ligada</div>;
      case 'OFFLINE':
        return <div className="text-status-offline flex flex-row justify-end">Offline</div>;
      case 'MAINTENANCE':
        return <div className="text-status-maintenance flex flex-row justify-end">Indisponível</div>;
      case 'STANDBY':
        return <div className="text-status-maintenance flex flex-row justify-end">Indisponível</div>;
      default:
        return <div className="text-status-offline flex flex-row justify-end">Offline</div>;
    }
  };

  function MaterialCardComponent({ material }: { material: MaterialDto }) {
    const [imageLoaded, setImageLoaded] = useState(false);
    const defaultImage = 'https://shelflix.com.br/wp-content/uploads/2025/02/Grey_Orange-300x90.png';
    const imageSrc = material.materialType?.photoUrl || defaultImage;

    const migrateMaterialMutation = useMutation(trpc.migration.materialMigration.mutationOptions());

    return (
      <Card
        key={material.uuid}
        className={cn(
          'flex cursor-pointer flex-row items-center gap-3 overflow-hidden rounded-[10px] px-0 py-0 transition-shadow duration-200'
        )}
        onClick={() => {
          onOpen?.();
          navigate(`${material.uuid}`, { state: { from: location.pathname + location.search } });
        }}
      >
        {!imageLoaded && (
          <Skeleton className="aspect-square max-w-[110px] min-w-[110px] rounded-l-sm bg-black object-contain" />
        )}
        <img
          src={imageSrc}
          alt={material.name}
          className={cn(
            'aspect-square max-w-[110px] cursor-pointer rounded-l-sm bg-black object-contain',
            !imageLoaded && 'hidden'
          )}
          onLoad={() => setImageLoaded(true)}
          onError={(e) => {
            (e.target as HTMLImageElement).src = defaultImage;
            setImageLoaded(true);
          }}
        />
        <div className="w-full p-1">
          <div className="mb-1 flex items-center justify-between">
            <h3 className="text-base font-semibold">{material.name}</h3>
            {material.externalId === null && (
              <Button
                className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-5 py-2.5 font-semibold text-white sm:grow-0"
                disabled={migrateMaterialMutation.isPending || !material.display?.externalId}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  migrateMaterialMutation.mutate(
                    {
                      materialUuid: material.uuid ?? '',
                      displayExternalId: material.display?.externalId ?? '',
                      scheduleCampaigns:
                        material.scheduleCampaigns.map((item) => {
                          return {
                            uuid: item.campaignUuid,
                            endDate: item.endDate,
                            startDate: item.startDate,
                          };
                        }) ?? [],
                    },
                    {
                      onSuccess: () => {
                        toast.success('Migração realizada com sucesso');
                      },
                      onError: (error) => {
                        toast.error(`Erro ao realizar migração: ${error.message}`);
                      },
                    }
                  );
                }}
              >
                <span className="[font-family:'Figtree'] text-sm">Migrar</span>
              </Button>
            )}
          </div>

          <div className="grid w-full grid-cols-2 gap-6">
            <div className="flex w-full flex-col">
              <span className="text-[10px] text-[#646464]">Campanha</span>
              <span className="text-[10px] text-[#646464]">ID Dispositivo</span>
              <span className="text-[10px] text-[#646464]">Status</span>
              <span className="text-[10px] text-[#646464]">Num Série</span>
            </div>
            <div className="flex w-full flex-col">
              <span className="truncate text-end text-[10px] text-[#CCCCCC]">
                {material.currentCampaign?.name || 'N/A'}
              </span>
              <span className="truncate text-end text-[10px] text-[#CCCCCC]">
                {material.display.externalId || 'N/A'}
              </span>
              <span className="truncate text-end text-[10px] text-[#CCCCCC]">
                {getStatusDisplayLabel(material.status)}
              </span>
              <span className="text-[#CCCCCC ] truncate text-end text-[10px]">{material.code || 'N/A'}</span>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="col-span-1 flex w-full flex-col gap-3 rounded-md bg-[var(--popover)]/60 px-2 py-4 min-[1040px]:w-auto">
      <h2 className="col-span-full flex items-start gap-2">
        <div className="h-6 w-2 rounded-full bg-[var(--primary)]"></div>
        <div className="flex flex-col gap-0.5">
          <div className="text-md font-semibold lg:text-xl">{establishment}</div>
        </div>
        <span className="ml-auto w-auto min-w-24 text-end text-xs font-normal text-gray-500">
          ({materials.length} materi{materials.length > 1 ? 'ais' : 'al'})
        </span>
      </h2>

      <div className="col-span-1 grid items-start gap-3 sm:grid-cols-1 md:grid-cols-2">
        {materials.map((material) => (
          <div className="col-span-1 flex flex-1 flex-col justify-start">
            <MaterialCardComponent key={material.uuid} material={material} />
          </div>
        ))}
      </div>
    </div>
  );
}
